import { getRealtimeVoiceServerUrl } from './realtimeVoice/config';
import { getJWTToken } from './auth';

const API_BASE_URL = getRealtimeVoiceServerUrl().replace(/\/ws\/control\/?$/, '').replace(/^ws/, 'http');

export interface UserProfile {
    id: string;
    email: string | null;
    full_name: string | null;
    nickname: string | null;
    referral_code: string | null;
    referrals_count_current_month: number;
    referral_monthly_limit: number;
    referral_reward_minutes: number;
    can_redeem_referral: boolean;
    subscription_balance_seconds: number;
    addon_balance_seconds: number;
    is_whitelisted: boolean;
    is_admin: boolean;
    has_completed_tutorial: boolean;
}

export async function getUserProfile(): Promise<UserProfile> {
    const token = getJWTToken();
    if (!token) throw new Error("Not authenticated");

    const response = await fetch(`${API_BASE_URL}/users/me`, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });

    if (!response.ok) {
        throw new Error('Failed to fetch profile');
    }

    return await response.json();
}

export async function redeemReferralCode(code: string): Promise<{ message: string; reward_seconds: number }> {
    const token = getJWTToken();
    if (!token) throw new Error("Not authenticated");

    const response = await fetch(`${API_BASE_URL}/users/redeem_referral`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ referral_code: code })
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to redeem code');
    }

    return await response.json();
}

export async function updateUserProfile(data: { nickname?: string; has_completed_tutorial?: boolean; timezone?: string }): Promise<UserProfile> {
    const token = getJWTToken();
    if (!token) throw new Error("Not authenticated");

    const response = await fetch(`${API_BASE_URL}/users/me`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
    });

    if (!response.ok) {
        throw new Error('Failed to update profile');
    }

    return await response.json();
}

export async function deleteUserAccountFromBackend(email: string): Promise<void> {
    const token = getJWTToken();
    if (!token) throw new Error("Not authenticated");
    if (!email) throw new Error("No email provided");

    const response = await fetch(`${API_BASE_URL}/api/admin/users/email/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });

    if (!response.ok && response.status !== 404) {
        throw new Error('Failed to delete account from server');
    }
}
